import { createSlice } from '@reduxjs/toolkit';
import { BUNDLE_STEP_CLEANUP_CONFIG, CREATE_BUNDLE_STEP_KEYS } from './createBundleConsts';


const createInitialState = () => ({
    currentStep: 0,
    isEditMode: false,
    editBundleId: null,
    originalSteps: null,
    steps: {
        [CREATE_BUNDLE_STEP_KEYS.BUNDLE_DETAILS]: {
            name: '',
            description: '',
            activityItemIds: [],
            paramTypes: []
        },
        [CREATE_BUNDLE_STEP_KEYS.PRICING]: {
            pricingItems: [],
            editingPricingKeys: [],
            somePricingHasUnsavedEdits: false
        },
        [CREATE_BUNDLE_STEP_KEYS.RULES]: {
            rulesList: [],
            editingRuleKeys: [],
            someRuleHasUnsavedEdits: false
        }
    }
});

const initialState = createInitialState();

export const bundleStateSlice = createSlice({
    name: 'bundleState',
    initialState,
    reducers: {
        setBundleCurrentStep: (state, action) => {
            state.currentStep = action.payload;
        },
        saveBundleStepData: (state, action) => {
            const { stepKey, data } = action.payload;
            if (!stepKey) return;
            state.steps[stepKey] = {
                ...state.steps[stepKey],
                ...data
            };
        },
        cleanupBundleStepDraft: (state, action) => {
            const { stepKey } = action.payload;
            const stepData = state.steps[stepKey];
            if (!stepData) return;

            const config = BUNDLE_STEP_CLEANUP_CONFIG[stepKey];
            if (!config) return;

            const { listKey, editingKey, unsavedFlag } = config;
            stepData[listKey] = (stepData[listKey] || []).filter(item => !item.isNew);
            stepData[editingKey] = [];
            stepData[unsavedFlag] = false;
        },
        initializeBundleForEdit: (state, action) => {
            const { editBundleId, bundleDetails, pricingItems, rulesList, prefill } = action.payload;
            state.isEditMode = true;
            state.editBundleId = editBundleId;
            // Optionally land the dialog directly on a specific step (e.g. open on the
            // Pricing step when adding pricing for an empty client/lot cell).
            state.currentStep = prefill?.startStep ?? 0;

            const bundleDetailsData = {
                ...createInitialState().steps[CREATE_BUNDLE_STEP_KEYS.BUNDLE_DETAILS],
                ...bundleDetails
            };

            // A prefilled new pricing item (client + specific lot already selected) is
            // prepended to the working set so the user only fills price. It is
            // intentionally left out of originalSteps so it registers as an unsaved change.
            const serverPricingItems = pricingItems || [];
            const workingPricingItems = prefill?.newPricingItem
                ? [prefill.newPricingItem, ...serverPricingItems]
                : serverPricingItems;

            const pricingData = {
                ...createInitialState().steps[CREATE_BUNDLE_STEP_KEYS.PRICING],
                pricingItems: workingPricingItems
            };
            const rulesData = {
                ...createInitialState().steps[CREATE_BUNDLE_STEP_KEYS.RULES],
                rulesList: rulesList || []
            };

            state.steps[CREATE_BUNDLE_STEP_KEYS.BUNDLE_DETAILS] = bundleDetailsData;
            state.steps[CREATE_BUNDLE_STEP_KEYS.PRICING] = pricingData;
            state.steps[CREATE_BUNDLE_STEP_KEYS.RULES] = rulesData;

            state.originalSteps = {
                [CREATE_BUNDLE_STEP_KEYS.BUNDLE_DETAILS]: structuredClone(bundleDetailsData),
                [CREATE_BUNDLE_STEP_KEYS.PRICING]: { pricingItems: structuredClone(serverPricingItems) },
                [CREATE_BUNDLE_STEP_KEYS.RULES]: { rulesList: structuredClone(rulesList || []) }
            };
        },
        resetCreateBundleState: () => createInitialState()
    }
});

export const {
    setBundleCurrentStep,
    saveBundleStepData,
    cleanupBundleStepDraft,
    initializeBundleForEdit,
    resetCreateBundleState
} = bundleStateSlice.actions;

export default bundleStateSlice.reducer;
